import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent} from 'ng-zorro-antd/form';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NzInputDirective} from 'ng-zorro-antd/input';
import {NzButtonComponent} from 'ng-zorro-antd/button';
import {NzMessageService} from 'ng-zorro-antd/message';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthService, MfaChallengeResponse} from '../services/auth.service';
import {HttpClientModule} from '@angular/common/http';
import {CommonModule} from '@angular/common';
import {NzIconModule} from "ng-zorro-antd/icon";
import {ADMIN_MFA_ACCESS_NOTICE_KEY, BE_API_AUTH_URL, MFA_REQUIRED_NOTICE_KEY, PASSWORD_CHANGED_NOTICE_KEY, SESSION_REVOKED_NOTICE_KEY} from '../constants/app.constants';
import {NzSpinModule} from 'ng-zorro-antd/spin';
import {NzRadioModule} from 'ng-zorro-antd/radio';
import {AppMode, DEFAULT_APP_MODE} from '../models/app-mode.model';
import {finalize, take} from 'rxjs/operators';
import { normalizeBackendMessageKey, shouldSuppressGenericError } from '../utils/http-error.utils';
import { RegistrationAttributionService } from '../services/registration-attribution.service';
import { en_US, de_DE, NzI18nService } from 'ng-zorro-antd/i18n';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { environment } from '../../environments/environment';
import { AuthReturnUrlService } from '../services/auth-return-url.service';
import { isMobileViewport } from '../utils/mobile-viewport.utils';
import QRCode from 'qrcode';

const TOTP_CODE_VALIDATORS = [Validators.required, Validators.pattern(/^\d{6}$/)];
const RECOVERY_CODE_VALIDATORS = [
  Validators.required,
  Validators.pattern(/^(?:[A-Za-z2-7]{4}[-\s]?){5}[A-Za-z2-7]{4}$/),
];

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    TranslateModule,
    NzInputDirective,
    NzButtonComponent,
    HttpClientModule,
    CommonModule,
    NzIconModule,
    NzSpinModule,
    NzRadioModule,
    NzCheckboxModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.less'
})
export class LoginComponent implements OnInit{
  loginForm!: FormGroup;
  mfaForm!: FormGroup;
  resetPasswordForm!: FormGroup;
  passwordVisible: boolean = false;
  isResetPasswordMode = false; // Add a flag for reset password mode
  isUpdatePasswordMode = false;
  signupForm!: FormGroup;
  codeForm!: FormGroup;
  isSignupMode = false;
  isVerifyingCode = false;
  signupEmail = '';
  signupPassword = '';
  signupCompany = '';
  signupAppMode: AppMode = 'emergency';
  signupPasswordVisible = false;
  passwordStrength: 'weak' | 'medium' | 'strong' | '' = '';
  isSubmittingLogin = false;
  isSubmittingSignup = false;
  isSubmittingReset = false;
  isSubmittingVerify = false;
  isMfaMode = false;
  isSubmittingMfa = false;
  mfaState: MfaChallengeResponse['mfaState'] | null = null;
  private mfaChallenge: string | null = null;
  mfaManualKey: string | null = null;
  mfaProvisioningUri: string | null = null;
  mfaQrCode: string | null = null;
  mfaRecoveryCodes: string[] = [];
  isShowingRecoveryCodes = false;
  useRecoveryCode = false;

  isSubmittingResend = false;
  isDirectRegistration = false;
  selectedLanguage = 'de';
  showSignupSuggestion = false;
  // BE messages we want to map to user-friendly FE translations
  private readonly MSG_COMPANY_INACTIVE =
    'Your account is inactive. Please contact contact@scanafy.de to renew your subscription.';

  private readonly MSG_USER_DEACTIVATED_GENERIC_DE =
    'Ihr Konto ist deaktiviert. Kontaktieren Sie contact@scanafy.de, wenn Sie es reaktivieren möchten.';

  private readonly MSG_DELETION_SCHEDULED_DE =
    'Ihr Konto ist deaktiviert, weil eine Kontolöschung geplant wurde. Bitte kontaktieren Sie contact@scanafy.de.';

  // simple resend cooldown to prevent spam-click
  private resendCooldownEndsAt: number | null = null;

  get isResendCooldownActive(): boolean {
    return this.resendCooldownEndsAt !== null && Date.now() < this.resendCooldownEndsAt;
  }

  get resendCooldownSeconds(): number {
    if (!this.isResendCooldownActive || this.resendCooldownEndsAt === null) return 0;
    return Math.ceil((this.resendCooldownEndsAt - Date.now()) / 1000);
  }

  constructor(private fb: FormBuilder,
              private router: Router,
              private message: NzMessageService,
              private authService: AuthService,
              private route: ActivatedRoute,
              private translate: TranslateService,
              private attributionService: RegistrationAttributionService,
              private nzI18nService: NzI18nService,
              private authReturnUrl: AuthReturnUrlService) {}

  ngOnInit(): void {
    this.initializeLanguage().pipe(take(1)).subscribe({
      next: () => {
        this.showPendingAuthenticationNotice();
      },
      error: () => {
        // Keep the one-time marker for a later successful language activation.
      }
    });
    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/),
        this.passwordByteLengthValidator,
      ]],
      companyName: ['', Validators.required],
      termsAccepted: [false, Validators.requiredTrue],
      // appMode: [DEFAULT_APP_MODE, Validators.required],
    });

    this.codeForm = this.fb.group({
      code: [
        '',
        [
          Validators.required,
          Validators.pattern(/^\d{6}$/)
        ]
      ]
    });

    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    this.mfaForm = this.fb.group({
      code: ['', TOTP_CODE_VALIDATORS],
      bootstrapCredential: [''],
    });

    const pendingMfaChallenge = this.authService.consumePendingMfaChallenge?.();
    if (pendingMfaChallenge?.mfaRequired && pendingMfaChallenge.mfaChallenge) {
      this.beginMfa(pendingMfaChallenge);
    }

    this.signupForm.get('password')?.valueChanges.subscribe(value => {
      this.passwordStrength = this.evaluatePasswordStrength(value);
    });

    // Form for resetting password
    this.resetPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    // Check if the user is already authenticated
    if (this.authService.isAuthenticated()) {
      const token = this.authService.getToken();
      if (token){
        const decodedToken = this.authService.decodeToken(token);
        const userRole = decodedToken?.role || '';

        // If mobile, show the handoff screen instead of going to dashboard/admin pages
        if (this.isMobile()) {
          this.router.navigate(['/mobile-desktop']);
          return;
        }

        // Redirect based on the user's role
        this.navigateAfterLoginByRole(userRole);
        return; // Prevent further execution if already logged in
      }
    }

    this.isDirectRegistration = this.route.snapshot.data['initialAuthMode'] === 'register'
      || this.route.snapshot.queryParamMap.get('mode') === 'register';
    if (this.isDirectRegistration) {
      this.isSignupMode = true;
      const entryRoute = this.route.snapshot.data['initialAuthMode'] === 'register'
        ? '/register'
        : '/login?mode=register';
      this.attributionService.capture(entryRoute);
    }

    // Capture the token from the URL and save it to local storage
    this.route.queryParams.subscribe((params) => {
      const token = params['token'];
      if (token) {
        this.isUpdatePasswordMode = true;
        this.authService.storeSession(token);

        // Decode the token to extract the role
        const decodedToken = this.authService.decodeToken(token);
        const userRole = decodedToken?.role || ''; // Extract the role from the decoded token

        if (this.isMobile()) {
          this.router.navigate(['/mobile-desktop']);
          return;
        }

        // Store the token in local storage and navigate based on the user's role
        // this.message.success(this.translate.instant('LOGIN.LOGIN_WITH_MICROSOFT_SUCCESS'));
        this.navigateAfterLoginByRole(userRole);
      }
    });
  }

  evaluatePasswordStrength(password: string): 'weak' | 'medium' | 'strong' | '' {
    if (!password) return '';

    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /\W/.test(password);

    const score = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

    if (password.length >= 8 && score >= 4) return 'strong';
    if (password.length >= 6 && score >= 2) return 'medium';
    return 'weak';
  }

  private passwordByteLengthValidator(control: AbstractControl): { passwordTooLong: true } | null {
    const password = typeof control.value === 'string' ? control.value : '';
    return new TextEncoder().encode(password).length > 72 ? { passwordTooLong: true } : null;
  }

  private isMobile(): boolean {
    return isMobileViewport();
  }

  private navigateAfterLoginByRole(userRole: string): void {
    if (this.authReturnUrl.navigateIfPresent(this.route.snapshot.queryParamMap.get('returnUrl'))) {
      return;
    }
    if (userRole === 'Admin') {
      this.router.navigate(['/admin/dashboard']);
      return;
    }
    if (userRole === 'User') {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.router.navigate(['/login']);
  }

  toggleResetPasswordMode(isReset: boolean): void {
    this.isResetPasswordMode = isReset;
  }

  toggleSignupPasswordVisibility(): void {
    this.signupPasswordVisible = !this.signupPasswordVisible;
  }

  toggleSignupMode(state: boolean) {
    this.isSignupMode = state;
    this.isVerifyingCode = false;
    if (!state) {
      this.showSignupSuggestion = false;
    }
  }

  onSignup(): void {
    if (this.isSubmittingSignup) {
      return;
    }

    if (this.signupForm.invalid) {
      this.signupForm.markAllAsTouched();
      return;
    }

    this.isSubmittingSignup = true;

    // Normalize email (removes leading/trailing spaces)
    const rawEmail = this.signupForm.value.email;
    const email = this.normalizeEmail(rawEmail);
    this.signupForm.patchValue({ email });

    const { password, companyName, termsAccepted } = this.signupForm.value;

    // Force emergency mode permanently
    const appMode: AppMode = 'emergency';

    this.signupEmail = email;
    this.signupPassword = password;
    this.signupCompany = companyName;
    this.signupAppMode = appMode;

    this.authService.signup(email, password, companyName, appMode, termsAccepted === true, this.attributionService.get())
      .pipe(finalize(() => { this.isSubmittingSignup = false; }))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('SIGNUP.CODE_SENT_SUCCESS'));
          this.isVerifyingCode = true;

          // cooldown after initial send as well
          this.startResendCooldown(30);
        },
        error: (err) => this.showSignupError(err)
      });
  }

  onVerifyCode(): void {
    if (this.codeForm.valid && !this.isSubmittingVerify) {
      this.isSubmittingVerify = true;
      const code = this.codeForm.value.code;
      const termsAccepted = this.signupForm.get('termsAccepted')?.value === true;
      this.authService.verifySignupCode(this.signupEmail, code, this.signupPassword, this.signupCompany, this.signupAppMode, termsAccepted).subscribe({
        next: (res) => {
          this.message.success(this.translate.instant('SIGNUP.VERIFICATION_SUCCESS'));
          this.authService.storeSession(res.token, res.appMode);
          this.attributionService.clear();
          this.pushRegistrationCompleteEvent();
          if (this.isMobile()) {
            this.router.navigate(['/mobile-desktop']);
          } else {
            this.router.navigate(['/dashboard']);
          }
        },
        error: err => {
          const code = err?.code || err?.error?.code;
          if (code === 'SIGNUP_CODE_INVALID') {
            this.message.error(this.translate.instant('SIGNUP.CODE_INVALID'));
          } else if (code === 'SIGNUP_CODE_EXPIRED') {
            this.message.error(this.translate.instant('SIGNUP.CODE_EXPIRED'));
          } else if (code === 'TERMS_NOT_ACCEPTED') {
            this.isVerifyingCode = false;
            this.isSignupMode = true;
            this.signupForm.get('termsAccepted')?.markAsTouched();
            this.message.error(this.translate.instant('SIGNUP.TERMS_REQUIRED'));
          } else {
            this.message.error(this.translate.instant('SIGNUP.VERIFICATION_FAILED'));
          }
          this.isSubmittingVerify = false;
        }
      });
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid && !this.isSubmittingLogin) {
      this.isSubmittingLogin = true;
      this.showSignupSuggestion = false;
      const { email, password } = this.loginForm.value;

      // Call the AuthService login method and subscribe to the response
      this.authService.login(email, password)
        .pipe(finalize(() => { this.isSubmittingLogin = false; }))
        .subscribe({
        next: (response) => {
          if (response.mfaRequired && response.mfaChallenge) {
            this.beginMfa(response);
            return;
          }
          if (!response.token) {
            this.message.error(this.translate.instant('LOGIN.INVALID_CREDENTIALS'));
            return;
          }
          this.message.success(this.translate.instant('LOGIN.LOGIN_SUCCESS'));

          // Decode the token to extract the role
          const token = response.token;
          const decodedToken = this.authService.decodeToken(token);
          const userRole = decodedToken?.role || '';


          // On mobile: go to the branded handoff screen (no dashboard on mobile)
          if (this.isMobile()) {
            this.router.navigate(['/mobile-desktop']);
            return;
          }

          // Desktop: Redirect based on user role
          if (userRole === 'Admin' || userRole === 'User') {
            this.navigateAfterLoginByRole(userRole);
          } else {
            this.message.error('Unknown user role.');
          }
        },
          error: (err) => {
            const messageKey = normalizeBackendMessageKey(err?.error?.messageKey || err?.messageKey || null);
            const errorCode = err?.error?.code || err?.code;
            const beMessage =
              err?.error?.message ||
              (typeof err?.error === 'string' ? err.error : '') ||
              err?.message ||
              '';

            // 1) Company inactive (billing / tenant lock)
            if (beMessage === this.MSG_COMPANY_INACTIVE) {
              this.message.error(this.translate.instant('LOGIN.INACTIVE_USER'));
              console.error(err);
              return;
            }

            // 2) User deactivated because deletion is scheduled
            if (beMessage === this.MSG_DELETION_SCHEDULED_DE) {
              this.message.error(this.translate.instant('LOGIN.DELETION_SCHEDULED_BLOCK'));
              console.error(err);
              return;
            }

            // 3) User deactivated (manual / admin disabled)
            if (beMessage === this.MSG_USER_DEACTIVATED_GENERIC_DE) {
              this.message.error(this.translate.instant('LOGIN.USER_DISABLED_BLOCK'));
              console.error(err);
              return;
            }

            if (messageKey === 'ERRORS.ACCOUNT_DEACTIVATED_BY_ADMIN') {
              this.message.error(this.translate.instant('ERRORS.ACCOUNT_DEACTIVATED_BY_ADMIN'));
              console.error(err);
              return;
            }

            if (errorCode === 'LOGIN_RATE_LIMITED') {
              this.message.error(this.translate.instant('LOGIN.TOO_MANY_LOGIN_ATTEMPTS'));
              return;
            }

            // 4) Fallback
            if (shouldSuppressGenericError(err)) {
              return;
            }
            if (err?.status === 401 && beMessage === 'Invalid email or password') {
              this.showSignupSuggestion = true;
            }
            this.message.error(this.translate.instant('LOGIN.INVALID_CREDENTIALS'));
            console.error(err);
          },
        });
    }
  }

  submitMfa(): void {
    if (this.isSubmittingMfa || !this.mfaChallenge) return;
    if (this.mfaState === 'initial_enrollment' || this.mfaState === 'replacement_enrollment' || this.mfaState === 'recovery_enrollment') {
      this.submitMfaEnrollment();
      return;
    }
    if (this.mfaForm.get('code')?.invalid) {
      this.mfaForm.get('code')?.markAsTouched();
      return;
    }
    this.isSubmittingMfa = true;
    const method = this.mfaState === 'recovery_verify' || this.useRecoveryCode ? 'recovery' : 'totp';
    this.authService.verifyMfa(this.mfaChallenge, this.mfaForm.value.code, method)
      .pipe(finalize(() => { this.isSubmittingMfa = false; }))
      .subscribe({
        next: (response) => {
          if (response.mfaRequired && response.mfaChallenge) {
            this.beginMfa(response);
            return;
          }
          this.completeMfaLogin(response);
        },
        error: () => this.message.error(this.translate.instant('LOGIN.MFA_VERIFICATION_FAILED')),
      });
  }

  private submitMfaEnrollment(): void {
    if (!this.mfaChallenge) return;
    if (!this.mfaManualKey) {
      if (this.mfaState === 'initial_enrollment' && !this.mfaForm.value.bootstrapCredential) {
        this.mfaForm.get('bootstrapCredential')?.markAsTouched();
        return;
      }
      this.isSubmittingMfa = true;
      this.authService.setupMfaEnrollment(this.mfaChallenge, this.mfaForm.value.bootstrapCredential)
        .pipe(finalize(() => { this.isSubmittingMfa = false; }))
        .subscribe({
          next: (setup) => {
            this.mfaManualKey = setup.manualKey;
            this.mfaProvisioningUri = setup.otpauthUri;
            QRCode.toDataURL(setup.otpauthUri, { errorCorrectionLevel: 'M', margin: 1 })
              .then((dataUrl) => { this.mfaQrCode = dataUrl; })
              .catch(() => { this.mfaQrCode = null; });
            this.mfaForm.get('bootstrapCredential')?.reset();
          },
          error: () => this.message.error(this.translate.instant('LOGIN.MFA_ENROLLMENT_FAILED')),
        });
      return;
    }
    if (this.mfaForm.get('code')?.invalid) {
      this.mfaForm.get('code')?.markAsTouched();
      return;
    }
    this.isSubmittingMfa = true;
    this.authService.confirmMfaEnrollment(this.mfaChallenge, this.mfaForm.value.code)
      .pipe(finalize(() => { this.isSubmittingMfa = false; }))
      .subscribe({
        next: (response) => this.completeMfaLogin(response),
        error: () => this.message.error(this.translate.instant('LOGIN.MFA_ENROLLMENT_FAILED')),
      });
  }

  private beginMfa(response: MfaChallengeResponse): void {
    this.isMfaMode = true;
    this.mfaState = response.mfaState || null;
    this.mfaChallenge = response.mfaChallenge || null;
    this.mfaManualKey = null;
    this.mfaProvisioningUri = null;
    this.mfaQrCode = null;
    this.mfaRecoveryCodes = [];
    this.useRecoveryCode = response.mfaState === 'recovery_verify';
    this.mfaForm.reset();
    this.setMfaCodeValidators();
  }

  switchMfaMethod(useRecoveryCode: boolean): void {
    this.useRecoveryCode = useRecoveryCode;
    this.mfaForm.get('code')?.reset();
    this.setMfaCodeValidators();
  }

  private setMfaCodeValidators(): void {
    const code = this.mfaForm.get('code');
    if (!code) return;
    code.setValidators(this.useRecoveryCode ? RECOVERY_CODE_VALIDATORS : TOTP_CODE_VALIDATORS);
    code.updateValueAndValidity();
  }

  private completeMfaLogin(response: MfaChallengeResponse): void {
    if (!response.token) {
      this.message.error(this.translate.instant('LOGIN.MFA_VERIFICATION_FAILED'));
      return;
    }
    this.authService.storeSession(response.token, response.appMode);
    this.mfaRecoveryCodes = response.recoveryCodes || [];
    this.mfaChallenge = null;
    if (this.mfaRecoveryCodes.length) {
      this.isShowingRecoveryCodes = true;
      return;
    }
    this.navigateAfterMfaLogin(response.token);
  }

  continueAfterRecoveryCodes(): void {
    this.mfaRecoveryCodes = [];
    this.isShowingRecoveryCodes = false;
    const token = this.authService.getToken();
    if (token) this.navigateAfterMfaLogin(token);
  }

  private navigateAfterMfaLogin(token: string): void {
    const role = this.authService.decodeToken(token)?.role || '';
    if (this.isMobile()) {
      this.router.navigate(['/mobile-desktop']);
      return;
    }
    this.navigateAfterLoginByRole(role);
  }

  onResetPasswordSubmit(): void {
    if (this.resetPasswordForm.valid && !this.isSubmittingReset) {
      this.isSubmittingReset = true;
      const { email } = this.resetPasswordForm.value;

      this.authService.resetPasswordRequest(email).subscribe({
        next: () => {
          this.message.success(this.translate.instant('LOGIN.RESET_PASSWORD_SUCCESS'));
          this.isSubmittingReset = false; // ✅ Reset the flag here
          this.resetPasswordForm.reset(); // Optional: reset form after success
          this.toggleResetPasswordMode(false); // Optional: go back to login
        },
        error: (err: any) => {
          this.message.error(this.translate.instant('LOGIN.RESET_PASSWORD_ERROR'));
          this.isSubmittingReset = false;
        },
      });
    }
  }

  loginWithMicrosoft(): void {
    window.location.href = `${BE_API_AUTH_URL}/microsoft`; // Redirect to backend Microsoft SSO route
  }

  private normalizeEmail(value: string): string {
    return (value || '').trim();
  }

  private pushRegistrationCompleteEvent(): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'scanafy_registration_complete'
    });
  }

  private startResendCooldown(seconds = 30): void {
    this.resendCooldownEndsAt = Date.now() + seconds * 1000;
    // force UI update every second while cooldown is active
    const tick = setInterval(() => {
      if (!this.isResendCooldownActive) {
        clearInterval(tick);
        this.resendCooldownEndsAt = null;
      }
    }, 1000);
  }

  private showSignupError(err: any): void {
    const code = err?.code || err?.error?.code;
    const msg = err?.message || err?.error?.message || '';

    // Backend error codes (see BE section)
    if (code === 'EMAIL_INVALID') {
      this.message.error(this.translate.instant('SIGNUP.EMAIL_INVALID'));
      return;
    }

    if (code === 'TERMS_NOT_ACCEPTED') {
      this.signupForm.get('termsAccepted')?.markAsTouched();
      this.message.error(this.translate.instant('SIGNUP.TERMS_REQUIRED'));
      return;
    }

    if (code === 'EMAIL_SEND_FAILED') {
      // friendly message, show the address to help the user spot typos
      this.message.error(this.translate.instant('SIGNUP.EMAIL_SEND_FAILED', { email: this.signupEmail }));
      return;
    }

    if (msg === 'User already exists' || code === 'USER_EXISTS') {
      this.message.error(this.translate.instant('SIGNUP.USER_EXISTS'));
      return;
    }

    this.message.error(this.translate.instant('SIGNUP.GENERIC_ERROR'));
  }

  editSignupDetails(): void {
    // return from verification screen to signup screen (keep values)
    this.isVerifyingCode = false;
    this.isSignupMode = true;

    this.signupForm.patchValue({
      email: this.signupEmail,
      password: this.signupPassword,
      companyName: this.signupCompany,
      // appMode: this.signupAppMode
    });
  }

  openSignupFromLogin(): void {
    // Prefill signup email from login email
    const email = this.normalizeEmail(this.loginForm?.value?.email || '');
    if (email) {
      this.signupForm.patchValue({ email });
    }

    // Switch modes
    this.isSignupMode = true;
    this.isResetPasswordMode = false;
    this.isVerifyingCode = false;
    this.showSignupSuggestion = false;

  }

  backToLogin(): void {
    this.isSignupMode = false;
    this.isVerifyingCode = false;
    this.isResetPasswordMode = false;
    this.showSignupSuggestion = false;
  }

  resendSignupCode(): void {
    if (this.isSubmittingResend || this.isResendCooldownActive) return;

    this.isSubmittingResend = true;

    this.authService.signup(
      this.signupEmail,
      this.signupPassword,
      this.signupCompany,
      this.signupAppMode,
      this.signupForm.get('termsAccepted')?.value === true,
      this.attributionService.get()
    )
      .pipe(finalize(() => { this.isSubmittingResend = false; }))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('SIGNUP.CODE_SENT_SUCCESS'));
          this.startResendCooldown(30);
        },
        error: (err) => this.showSignupError(err)
      });
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;  // Toggle password visibility
  }

  get termsUrl(): string {
    return `${environment.domain}${this.selectedLanguage === 'de' ? '/agb' : '/terms'}`;
  }

  switchLanguage(language: 'de' | 'en') {
    this.selectedLanguage = language;
    this.translate.setDefaultLang(language);
    const languageActivation = this.translate.use(language);
    this.nzI18nService.setLocale(language === 'en' ? en_US : de_DE);
    localStorage.setItem('selectedLanguage', language);
    return languageActivation;
  }

  private initializeLanguage() {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    return this.switchLanguage(savedLanguage === 'en' ? 'en' : 'de');
  }

  private showPendingAuthenticationNotice(): void {
    const adminMfaNotice = sessionStorage.getItem(ADMIN_MFA_ACCESS_NOTICE_KEY);
    if (adminMfaNotice) {
      const key = adminMfaNotice === 'sso' ? 'LOGIN.ADMIN_MFA_SSO_DENIED' : 'LOGIN.ADMIN_MFA_DESKTOP_DENIED';
      this.translate.get(key).pipe(take(1)).subscribe({
        next: (translatedText) => {
          if (typeof translatedText !== 'string' || translatedText === key) return;
          sessionStorage.removeItem(ADMIN_MFA_ACCESS_NOTICE_KEY);
          this.message.error(translatedText);
        },
      });
      return;
    }
    if (sessionStorage.getItem(PASSWORD_CHANGED_NOTICE_KEY) === '1') {
      this.translate.get('LOGIN.PASSWORD_CHANGED_RELOGIN').pipe(take(1)).subscribe({
        next: (translatedText) => {
          if (typeof translatedText !== 'string' || translatedText === 'LOGIN.PASSWORD_CHANGED_RELOGIN') {
            return;
          }

          sessionStorage.removeItem(PASSWORD_CHANGED_NOTICE_KEY);
          sessionStorage.removeItem(SESSION_REVOKED_NOTICE_KEY);
          sessionStorage.removeItem(MFA_REQUIRED_NOTICE_KEY);
          this.message.success(translatedText);
        },
        error: () => {
          // Keep the marker so a later login load can retry.
        }
      });
      return;
    }

    if (sessionStorage.getItem(SESSION_REVOKED_NOTICE_KEY) !== '1') {
      if (sessionStorage.getItem(MFA_REQUIRED_NOTICE_KEY) !== '1') return;
      this.translate.get('LOGIN.MFA_REQUIRED_RELOGIN').pipe(take(1)).subscribe({
        next: (translatedText) => {
          if (typeof translatedText !== 'string' || translatedText === 'LOGIN.MFA_REQUIRED_RELOGIN') return;
          sessionStorage.removeItem(MFA_REQUIRED_NOTICE_KEY);
          this.message.error(translatedText);
        },
      });
      return;
    }

    this.translate.get('LOGIN.SESSION_REVOKED').pipe(take(1)).subscribe({
      next: (translatedText) => {
        if (typeof translatedText !== 'string' || translatedText === 'LOGIN.SESSION_REVOKED') {
          return;
        }

        sessionStorage.removeItem(SESSION_REVOKED_NOTICE_KEY);
        sessionStorage.removeItem(MFA_REQUIRED_NOTICE_KEY);
        this.message.error(translatedText);
      },
      error: () => {
        // Keep the marker so a later login load can retry.
      }
    });
  }
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}
